/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClusterActionService, WebSocketConnection } from '@app/core/services';
import { completedClusterAction } from '@common/rxjs-operators/completed-cluster-action';
import { ClusterId } from '@shared/types/cluster';
import { ActionName } from '@shared/types/cluster-actions';
import {
  GlobalQueryId,
  Query,
  QueryAllSessionIdsResponse,
  QuerySessionInfo,
  QuerySessionPaged,
  QuerySessionResponse,
  SqlMetadata,
} from '@shared/types/queries';
import { omit } from 'lodash-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { RunningQueriesMonitoringFilter } from './types';

const PAGINATION_SESSION_ID = uuid();

@Injectable({ providedIn: 'root' })
export class QueryService {
  constructor(
    private http: HttpClient,
    private ws: WebSocketConnection,
    private clusterActions: ClusterActionService,
  ) {}

  create = (query: Query) => this.http.put('api/v1/query', query);

  update = (query: Query) => this.http.patch(`api/v1/query/${query.id}`, omit(query, 'id'));

  delete = (queryId: string) => this.http.delete<void>(`api/v1/query/${queryId}`);

  get = (queryId: string) => this.http.get<Query>(`api/v1/query/${queryId}`);

  getAll = () => this.http.get<Query[]>(`api/v1/queries`);

  getSqlMetadata = (clusterId: string): Observable<SqlMetadata[]> =>
    this.ws.getTopic(`/topic/cluster/caches/sql-meta/${clusterId}`);

  querySessions = (
    clusterId: ClusterId,
    page: number,
    size: number,
    filter: RunningQueriesMonitoringFilter,
  ): Observable<QuerySessionResponse> => {
    const params: Record<string, any> = {
      page: page.toString(),
      size: size.toString(),
      paginationSessionId: PAGINATION_SESSION_ID,
      ...filter,
    };
    return this.http.get<QuerySessionResponse>(`/api/v1/query/${clusterId}/query-sessions`, {
      params,
    });
  };

  getAllQuerySessionIds = (clusterId: ClusterId, filter: RunningQueriesMonitoringFilter) => {
    return this.http.get<QueryAllSessionIdsResponse>(`/api/v1/query/${clusterId}/query-sessions-all`, {
      params: filter,
    });
  };

  getQueryInfo = (clusterId: ClusterId, queryId: GlobalQueryId) => {
    return this.http.get<QuerySessionInfo>(`/api/v1/query/${clusterId}/query-sessions/${queryId}`);
  };

  newQueryCounterUpdates = (clusterId: ClusterId): Observable<number> => {
    return this.ws.getTopic<number>(`/topic/query/${clusterId}/running-queries`);
  };

  querySessionUpdates = (clusterId: ClusterId, queryId: GlobalQueryId): Observable<QuerySessionPaged> => {
    return this.ws.getTopic<QuerySessionPaged>(`/topic/query/${clusterId}/running-queries/${queryId}`);
  };

  getRunningQueryConfiguration(clusterId: ClusterId) {
    return this.clusterActions.dispatch(ActionName.GET_RUNNING_QUERY_CONFIGURATION, { clusterId }).pipe(
      completedClusterAction(),
      map((res) => res.result),
    );
  }
}
